import React from 'react'
import { IconButton, Card } from '../common'

const SortIcon = ({ column, sortConfig }) => (
  <svg
    className={`w-4 h-4 transition-transform duration-200 ${sortConfig.key === column && sortConfig.direction === 'desc' ? 'rotate-180' : ''
      } ${sortConfig.key === column ? 'text-green-700' : 'text-slate-400'}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4" />
  </svg>
)

const StatusBadge = ({ status }) => {
  const s = status || 'Active'
  const styles = {
    'Active': 'bg-green-100 text-green-800',
    'Inactive': 'bg-red-100 text-red-800',
    'On Leave': 'bg-yellow-100 text-yellow-800',
    'Resigned': 'bg-orange-100 text-orange-800',
    'Terminated': 'bg-gray-100 text-gray-800'
  }
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[s] || 'bg-gray-100 text-gray-800'}`}>
      {s}
    </span>
  )
}

const ProjectBadge = ({ project }) => (
  <div className="text-xs">
    <div className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-green-100 text-green-800" title={project.name}>
      {project.name}
    </div>
  </div>
)

const WorkforceTable = ({
  employees,
  sortConfig,
  onSort,
  onRowClick,
  onRightClick,
  expandedRows,
  onToggleExpand,
  allocationLoading,
  allocationError,
  getEmployeeCurrentProjects,
  formatExperience,
  hasPermission,
  loadMoreTriggerRef,
  pagination,
  loadingMore
}) => {
  const renderProjectsCell = (employeeId) => {    
    const projects = getEmployeeCurrentProjects(employeeId)
    if (projects.length === 0) return <span className="text-xs text-slate-400">No projects</span>

    return (
      <div className="space-y-1">
        {projects.slice(0, 3).map(project => (
          <ProjectBadge key={project.id} project={project} />
        ))}
        {projects.length > 3 && (
          <div className="text-xs text-slate-400">+{projects.length - 3} more</div>
        )}
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-xl border-2 border-slate-200 shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-gradient-to-r from-slate-50 to-slate-100">
          <tr>
            <th className="text-left py-3 px-4 font-semibold text-slate-700 w-8"></th>
            {[
              { key: 'employee_id', label: 'Employee ID', sortable: true, mono: true },
              { key: 'name', label: 'Name', sortable: true, bold: true },
              { key: 'email', label: 'Email' },
              { key: 'parent_department', label: 'Parent Department', sortable: true, badge: true },
              { key: 'department', label: 'Department', sortable: true, badge: true },
              { key: 'designation', label: 'Role', sortable: true },
              { key: 'reporting_manager_name', label: 'Manager' },
              { key: 'total_experience', label: 'Experience', sortable: true },
              { key: 'billing_rate', label: 'Billing Rate' },
              { key: 'projects', label: 'Projects' },
              { key: 'status', label: 'Status' },
            ].map(col => (
              <th
                key={col.key}
                className={`text-left py-3 px-4 font-semibold text-slate-700 ${col.sortable ? 'cursor-pointer hover:bg-slate-200 transition-all duration-200 group' : ''}`}
                onClick={col.sortable ? () => onSort(col.key) : undefined}
              >
                <div className="flex items-center gap-2 whitespace-nowrap">
                  {col.label}
                  {col.sortable && <SortIcon column={col.key} sortConfig={sortConfig} />}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-200">
          {employees.map((employee) => {
            const isExpanded = expandedRows.has(employee.id)
            const fullName = `${employee.first_name || employee.firstName || ''} ${employee.last_name || employee.lastName || ''}`.trim()
            
            return (
              <React.Fragment key={employee.id}>
                <tr
                  className={`hover:bg-green-50 transition-all duration-200 group ${
                    hasPermission('workforce-table-click') ? 'cursor-pointer' : 'cursor-default'
                  }`}
                  onClick={hasPermission('workforce-table-click') ? () => onRowClick(employee) : undefined}
                  onContextMenu={hasPermission('workforce-table-click') ? (e) => onRightClick(e, employee) : undefined}
                >
                  <td className="py-3 px-4">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleExpand(employee.id)
                      }}
                      size="sm"
                    >
                      <svg
                        className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </IconButton>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded text-slate-700">
                      {employee.employee_id || employee.employeeId || employee.id}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-green-700 hover:text-green-800 font-semibold transition-all duration-200">
                      {fullName}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-600">
                    <div className="truncate max-w-[200px]" title={employee.email || 'N/A'}>
                      {employee.email || 'N/A'}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium text-green-800">
                      {employee.parent_department || 'N/A'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-green-100 text-green-800">
                      {employee.department || 'N/A'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{employee.designation || employee.title || 'N/A'}</td>
                  <td className="py-3 px-4 text-slate-600 whitespace-nowrap">
                    {employee.reporting_manager_name || employee.manager || 'N/A'}
                  </td>
                  <td className="py-3 px-4 text-slate-600 whitespace-nowrap">
                    {formatExperience(employee.total_experience)}
                  </td>
                  <td className="py-3 px-4 text-slate-600">$0/hr</td>
                  <td className="py-3 px-4">
                    {renderProjectsCell(employee.id)}
                  </td>
                  <td className="py-3 px-4">
                    <StatusBadge status={employee.employee_status} />
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan="12" className="px-4 py-4 bg-slate-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <Card title="Skills Summary">
                          <p>No skills data available</p>
                        </Card>

                        <Card title="Current Projects">
                          {(() => {
                            const currentProjects = getEmployeeCurrentProjects(employee.id)
                            if (allocationLoading) return <p>Loading project assignments...</p>
                            if (allocationError) return <p className="text-red-500">Error loading projects</p>
                            if (currentProjects.length === 0) return <p>No project assignments</p>
                            
                            return (
                              <div className="space-y-2">
                                {currentProjects.map((project) => (
                                  <div key={project.id} className="flex justify-between items-center p-2 bg-slate-50 border border-slate-200 rounded">
                                    <div>
                                      <div className="font-medium text-slate-700">{project.name}</div>
                                      <div className="text-xs text-slate-500">
                                        {project.status || 'Active'} {project.client ? `• ${project.client}` : ''}
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="font-semibold text-green-700">
                                        {project.allocation_percentage}%
                                      </div>
                                      {project.start_date && project.end_date && (
                                        <div className="text-xs text-slate-500">
                                          {new Date(project.start_date).toLocaleDateString()}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )
                          })()}
                        </Card>

                        <Card title="Rate History">
                          <p>No rate history available</p>
                        </Card>

                        <Card title="Documents">
                          <p>No documents uploaded</p>
                        </Card>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
      
      {pagination.hasMore && (
        <div ref={loadMoreTriggerRef} className="flex items-center justify-center py-8">
          {loadingMore && (
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-700"></div>
              <span className="text-slate-600 text-sm">Loading more employees...</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default WorkforceTable
